import { getAnalytics, logEvent, setUserId, setUserProperties, type Analytics as FirebaseAnalytics } from 'firebase/analytics';
import { getPerformance, trace } from 'firebase/performance';
import type { FirebaseApp } from 'firebase/app';
import { isLocal } from './constants';

let analytics: FirebaseAnalytics | null = null;
let performance: ReturnType<typeof getPerformance> | null = null;

/**
 * Initialize Firebase Analytics and Performance Monitoring
 */
export function initializeAnalytics(app: FirebaseApp) {
    if (!isLocal) {
        analytics = getAnalytics(app);
        performance = getPerformance(app);
        console.log('📊 Analytics and Performance Monitoring initialized');
    } else {
        console.log('📊 Analytics disabled in local development');
    }
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, params?: Record<string, any>) {
    if (!analytics) return;

    try {
        logEvent(analytics, eventName, params);
        if (!isLocal) {
            console.log(`📊 Event: ${eventName}`, params);
        }
    } catch (error) {
        console.error('Failed to track event:', error);
    }
}

/**
 * Set user ID for analytics
 */
export function setAnalyticsUserId(userId: string | null) {
    if (!analytics) return;

    try {
        setUserId(analytics, userId);
    } catch (error) {
        console.error('Failed to set user ID:', error);
    }
}

/**
 * Set user properties
 */
export function setAnalyticsUserProperties(properties: Record<string, any>) {
    if (!analytics) return;

    try {
        setUserProperties(analytics, properties);
    } catch (error) {
        console.error('Failed to set user properties:', error);
    }
}

/**
 * Start a performance trace
 */
export function startTrace(traceName: string) {
    if (!performance) return null;

    try {
        const t = trace(performance, traceName);
        t.start();
        return t;
    } catch (error) {
        if (!isLocal) {
            console.error('Failed to start trace:', error);
        }
        return null;
    }
}

/**
 * Stop a performance trace
 */
export function stopTrace(t: ReturnType<typeof trace> | null, attributes?: Record<string, string>) {
    if (!t) return;

    try {
        if (attributes) {
            Object.entries(attributes).forEach(([key, value]) => {
                t.putAttribute(key, value);
            });
        }
        t.stop();
    } catch (error) {
        if (!isLocal) {
            console.error('Failed to stop trace:', error);
        }
    }
}

// ============================================================================
// Pre-defined Event Tracking Functions
// ============================================================================

// User Lifecycle Events
export const Analytics = {
    // User lifecycle
    userSignup: (method: string) => trackEvent('user_signup', { method }),
    userLogin: (method: string) => trackEvent('user_login', { method }),
    userLogout: () => trackEvent('user_logout'),

    // Core features
    passageView: (book: string, ref: string, passageId: string) =>
        trackEvent('passage_view', { book, ref, passage_id: passageId }),

    passageNavigation: (direction: 'next' | 'previous', fromPassage: string, toPassage: string, toBook: string, toRef: string) =>
        trackEvent('passage_navigation', { direction, from_passage: fromPassage, to_passage: toPassage, to_book: toBook, to_ref: toRef }),

    contextPanelOpen: (type: 'cultural' | 'archaeological' | 'literary' | 'people' | 'historical') =>
        trackEvent('context_panel_open', { context_type: type }),

    inductiveView: (passageId: string) =>
        trackEvent('study_guide_view', { passage_id: passageId }),

    guidedStudyStart: (passageId: string, personaId: string) =>
        trackEvent('guided_study_start', { passage_id: passageId, persona_id: personaId }),

    guidedStudyComplete: (passageId: string, personaId: string, durationSeconds: number) =>
        trackEvent('guided_study_complete', {
            passage_id: passageId,
            persona_id: personaId,
            duration_seconds: durationSeconds
        }),

    guidedStudyStepComplete: (passageId: string, personaId: string, stepIndex: number) =>
        trackEvent('guided_study_step_complete', {
            passage_id: passageId,
            persona_id: personaId,
            step_index: stepIndex
        }),

    // Persona/Onboarding
    personaSelected: (personaId: string, method: 'onboarding' | 'manual' | 'auto') =>
        trackEvent('persona_selected', { persona_id: personaId, selection_method: method }),

    onboardingComplete: (personaId: string) =>
        trackEvent('onboarding_complete', { persona_id: personaId }),

    onboardingSkipped: () =>
        trackEvent('onboarding_skipped'),

    // Map interactions
    mapInteraction: (action: 'zoom' | 'pan' | 'marker_click' | 'layer_toggle', details?: Record<string, any>) =>
        trackEvent('map_interaction', { action, ...details }),

    // Timeline interactions
    timelineInteraction: (action: 'period_select' | 'event_click' | 'zoom' | 'pan', details?: Record<string, any>) =>
        trackEvent('timeline_interaction', { action, ...details }),

    // Search
    search: (query: string, resultsCount: number) =>
        trackEvent('search', { query, results_count: resultsCount }),

    // Feedback
    feedbackSubmitted: (type: string, hasContext: boolean) =>
        trackEvent('feedback_submitted', { feedback_type: type, has_context: hasContext }),

    // Errors
    error: (type: string, message: string, page: string, stack?: string) =>
        trackEvent('error', { error_type: type, error_message: message, page, stack }),
};

